import React, { useEffect, useState } from "react";
import { Ticket, Trash2, Edit2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BLANK = { code: "", discount_type: "amount", value: "", max_uses: "", min_delegates: "", active: true };

const describe = (c) => (c.discount_type === "amount" ? `₹${Number(c.value).toLocaleString("en-IN")} off each delegate` : `${c.value}% off the total`);

const Lbl = ({ text, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase font-bold text-slate-500 mb-1">{text}</span>
    {children}
  </label>
);

const CouponForm = ({ draft, setDraft, onSave, onCancel, saving }) => (
  <div className="dashboard-card p-4 space-y-3" data-testid="coupon-form">
    <div className="font-semibold text-sm text-slate-900">{draft.coupon_id ? "Edit coupon" : "New coupon"}</div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
      <Lbl text="Coupon code">
        <input className={`input-pill w-full ${draft.code ? "uppercase" : ""}`} placeholder="e.g. EARLY20" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input" />
      </Lbl>
      <Lbl text="Discount type">
        <select className="input-pill w-full" value={draft.discount_type} onChange={(e) => setDraft({ ...draft, discount_type: e.target.value })} data-testid="coupon-type-select">
          <option value="amount">₹ off each delegate</option>
          <option value="percent">% off the total</option>
        </select>
      </Lbl>
      <Lbl text={draft.discount_type === "amount" ? "Amount (₹)" : "Percent (%)"}>
        <input type="number" min="0" className="input-pill w-full" placeholder="0" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} data-testid="coupon-value-input" />
      </Lbl>
      <Lbl text="Usage limit (payments)">
        <input type="number" min="1" className="input-pill w-full" placeholder="Unlimited" value={draft.max_uses ?? ""} onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })} data-testid="coupon-max-uses-input" />
      </Lbl>
      <Lbl text="Min delegates">
        <input type="number" min="1" className="input-pill w-full" placeholder="Optional" value={draft.min_delegates ?? ""} onChange={(e) => setDraft({ ...draft, min_delegates: e.target.value })} data-testid="coupon-min-delegates-input" />
      </Lbl>
    </div>
    <div className="flex items-center gap-2">
      <button onClick={onSave} disabled={saving} className="btn-primary text-sm" data-testid="coupon-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Save coupon
      </button>
      {draft.coupon_id && <button onClick={onCancel} className="btn-ghost text-sm" data-testid="coupon-cancel-btn">Cancel</button>}
    </div>
  </div>
);

const CouponRow = ({ c, onToggle, onEdit, onDelete }) => (
  <div className="flex items-center gap-3 p-3 border-b border-slate-100 last:border-0 flex-wrap" data-testid={`coupon-row-${c.code}`}>
    <span className="font-mono font-bold text-slate-900 px-2 py-1 rounded-lg bg-orange-50 border border-orange-200">{c.code}</span>
    <div className="flex-1 min-w-[180px] text-xs text-slate-600">
      <div className="font-semibold text-slate-800">{describe(c)}</div>
      <div>Used {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""} payments{c.min_delegates ? ` · min ${c.min_delegates} delegates` : ""}</div>
    </div>
    <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
      <input type="checkbox" checked={c.active !== false} onChange={() => onToggle(c)} className="w-4 h-4 accent-emerald-600" data-testid={`coupon-toggle-${c.code}`} />
      {c.active !== false ? "On" : "Off"}
    </label>
    <button onClick={() => onEdit(c)} className="btn-ghost text-xs py-1.5" data-testid={`coupon-edit-${c.code}`}><Edit2 className="w-3 h-3" /></button>
    <button onClick={() => onDelete(c)} className="btn-ghost text-xs py-1.5 text-rose-600" data-testid={`coupon-delete-${c.code}`}><Trash2 className="w-3 h-3" /></button>
  </div>
);

export const FormCouponsPanel = ({ eventId }) => {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/events/${eventId}/coupons`).then((r) => setRows(r.data || [])).catch(() => toast.error("Failed to load coupons"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const save = async () => {
    setSaving(true);
    const body = { ...draft, value: Number(draft.value) };
    try {
      if (draft.coupon_id) await api.patch(`/coupons/${draft.coupon_id}`, body);
      else await api.post(`/events/${eventId}/coupons`, body);
      toast.success("Coupon saved");
      setDraft(BLANK);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const toggle = async (c) => {
    const next = c.active === false;
    setRows((rs) => rs.map((x) => (x.coupon_id === c.coupon_id ? { ...x, active: next } : x)));
    try {
      const r = await api.patch(`/coupons/${c.coupon_id}`, { active: next });
      setRows((rs) => rs.map((x) => (x.coupon_id === c.coupon_id ? r.data : x)));
    } catch (e) {
      setRows((rs) => rs.map((x) => (x.coupon_id === c.coupon_id ? c : x)));
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await api.delete(`/coupons/${c.coupon_id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="coupons-panel">
      <p className="text-xs text-slate-500">Coupons work on every form of this event only. The usage limit counts successful payments.</p>
      <CouponForm draft={draft} setDraft={setDraft} onSave={save} onCancel={() => setDraft(BLANK)} saving={saving} />
      <div className="dashboard-card">
        {rows.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">No coupons yet.</div> :
          rows.map((c) => <CouponRow key={c.coupon_id} c={c} onToggle={toggle} onEdit={(x) => setDraft({ ...x, max_uses: x.max_uses ?? "", min_delegates: x.min_delegates ?? "" })} onDelete={remove} />)}
      </div>
    </div>
  );
};
